
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import UpdatePasswordForm from "@/components/auth/UpdatePasswordForm";

const hasRecoveryParams = () => {
  const hash = window.location.hash;
  const search = window.location.search;
  return (
    hash.includes("type=recovery") ||
    hash.includes("access_token") ||
    hash.includes("refresh_token") ||
    search.includes("type=recovery") ||
    search.includes("token_hash") ||
    search.includes("code=")
  );
};

const getFriendlyPasswordError = (message: string) => {
  const lower = message.toLowerCase();
  if (lower.includes("weak") || lower.includes("pwned") || lower.includes("known to be")) {
    return "Ce mot de passe est refusé car il est trop courant ou compromis. Essayez une phrase de passe unique, plus longue.";
  }
  return message;
};

const ResetPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [mode, setMode] = useState<"request" | "checking" | "update">("request");
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const recoveryLink = hasRecoveryParams();
    if (recoveryLink) setMode("checking");

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || (event === "SIGNED_IN" && recoveryLink)) {
        setMode("update");
      }
    });

    const checkSession = async () => {
      if (!recoveryLink) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setMode("update");
        return;
      }
      window.setTimeout(async () => {
        const { data: { session: delayedSession } } = await supabase.auth.getSession();
        setMode(delayedSession ? "update" : "request");
        if (!delayedSession) {
          toast({
            title: "Lien invalide",
            description: "Le lien de réinitialisation est expiré. Demandez un nouveau lien.",
            variant: "destructive",
          });
        }
      }, 1800);
    };

    checkSession();
    return () => subscription.unsubscribe();
  }, [toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("ResetPassword: Starting password reset process for:", email);
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      console.log("ResetPassword: Reset password response:", { data, error });

      if (error) {
        console.error("ResetPassword: Error during reset:", error);
        throw error;
      }
      
      console.log("ResetPassword: Reset email sent successfully");
      toast({
        title: "Email envoyé",
        description: "Vérifiez votre boîte mail pour réinitialiser votre mot de passe.",
      });
      
      navigate("/account");
    } catch (error: any) {
      console.error("ResetPassword: Caught error:", error);
      toast({
        title: "Erreur",
        description: error.message || "Une erreur est survenue lors de la réinitialisation du mot de passe.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async (password: string) => {
    setIsUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        return { success: false, message: getFriendlyPasswordError(error.message) };
      }

      toast({
        title: "Mot de passe mis à jour",
        description: "Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.",
      });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        message: getFriendlyPasswordError(error.message || "Une erreur est survenue."),
      };
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  if (mode === "checking") {
    return (
      <div className="container mx-auto px-4 py-8 max-w-md text-center">
        <h1 className="text-2xl font-bold mb-3">Validation du lien...</h1>
        <p className="text-muted-foreground">Veuillez patienter quelques secondes.</p>
      </div>
    );
  }

  if (mode === "update") {
    return (
      <div className="container mx-auto px-4 py-8">
        <UpdatePasswordForm onSubmit={handleUpdatePassword} loading={isUpdatingPassword} />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-md">
      <h1 className="text-2xl font-bold mb-6">Réinitialisation du mot de passe</h1>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-2">
            Adresse email
          </label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="votreemail@exemple.com"
            required
            className="w-full"
          />
        </div>
        
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? "Envoi en cours..." : "Réinitialiser le mot de passe"}
        </Button>

        <p className="text-sm text-gray-600 text-center mt-4">
          Vous vous souvenez de votre mot de passe ?{" "}
          <a href="/account" className="text-primary hover:underline">
            Retour à la connexion
          </a>
        </p>

      </form>
    </div>
  );
};

export default ResetPassword;
